import type { TabKey } from './TabBar';

/** The immersive dark fill used by the picks flow, the live board and fixture detail.
 *  Kept here so the page-transition layer can paint the same colour behind/under a
 *  dark screen — otherwise the light app surface flashes through as a white seam
 *  while two dark screens slide past each other. `md:` clears it back to the light
 *  desktop canvas. */
export const IMMERSIVE_DARK_SURFACE =
  'bg-[linear-gradient(179deg,#1A1A22_0%,#0C0C11_58%,#08080C_100%)] md:bg-none md:bg-canvas';

/** The :leagueId segment of a /play/:leagueId... path, if the route carries one. */
export function leagueIdFromPath(pathname: string): string | null {
  const m = pathname.match(/^\/play\/([^/]+)/);
  return m ? decodeURIComponent(m[1]) : null;
}

/**
 * Where each tab points inside the active league world. With no resolvable league
 * (a brand-new player) every scoped tab falls back to the lobby.
 */
export function tabRoute(key: TabKey, leagueId: string | null): string {
  if (!leagueId) return key === 'play' ? '/' : '/football';
  const base = `/play/${leagueId}`;
  switch (key) {
    case 'play':
      return base;
    case 'standings':
      return `${base}/standings`;
    case 'friends':
      return `${base}/friends`;
    case 'profile':
      return `${base}/profile`;
  }
}

/** Which tab a given pathname belongs to (the play tab owns the sport/league/play flow). */
export function tabForPath(pathname: string): TabKey {
  if (/^\/play\/[^/]+\/standings/.test(pathname) || pathname.startsWith('/standings')) return 'standings';
  if (
    /^\/play\/[^/]+\/friends/.test(pathname) ||
    pathname.startsWith('/friends') ||
    pathname.startsWith('/duel') ||
    pathname.startsWith('/challenge') ||
    pathname.startsWith('/u/') ||
    pathname.startsWith('/leagues')
  )
    return 'friends';
  if (
    /^\/play\/[^/]+\/(profile|notifications)/.test(pathname) ||
    pathname.startsWith('/profile') ||
    pathname.startsWith('/settings') ||
    pathname.startsWith('/notifications')
  )
    return 'profile';
  return 'play';
}

/**
 * Screens where the bottom tab bar is suppressed:
 *   - the pre-league lobby (`/` sport picker, `/football` league picker) — you
 *     haven't entered a competition yet, so the league-scoped Standings/Friends
 *     tabs have no real context; navigation here is the back-arrow + avatar.
 *   - the picks flow, the immersive dark "Watch live" board, and the dark fixture
 *     detail, where the tab bar would compete with the primary CTA / break the
 *     full-bleed dark surface.
 */
export function hideTabBarForPath(pathname: string): boolean {
  if (pathname === '/' || /^\/football\/?$/.test(pathname)) return true;
  if (/^\/play\/[^/]+\/fixture\//.test(pathname)) return true;
  // The challenge-setup flow is a focused, single-purpose task (compose a duel and
  // send it) — the league-scoped tabs would only distract from the primary CTA.
  if (pathname.startsWith('/challenge/')) return true;
  return /^\/play\/[^/]+\/(picks|waiting|results)\/?$/.test(pathname);
}

/**
 * A "tab root" is one of the destinations the bottom tab bar / side rail navigate
 * to directly. Moving *between* two tab roots is a lateral tab switch (instant cut,
 * UITabBarController-style); anything else (drilling into a detail, the picks flow,
 * a duel) is a push/pop that slides. Keeping this list explicit is what lets the
 * transition layer tell "switched tabs" apart from "went deeper".
 */
export function isTabRoot(pathname: string): boolean {
  if (pathname === '/' || /^\/football\/?$/.test(pathname)) return true;
  return /^\/play\/[^/]+(\/(standings|friends|profile))?\/?$/.test(pathname);
}

/**
 * The immersive, full-bleed dark screens (picks flow, the "Watch live" board and
 * fixture detail). The page-transition layer reads this to paint the dark fill on
 * the sliding panel *and* the viewport behind it, so navigating between two dark
 * screens never flashes the light app surface through the seam.
 */
export function isDarkRoute(pathname: string): boolean {
  if (/^\/play\/[^/]+\/fixture\//.test(pathname)) return true;
  return /^\/play\/[^/]+\/(picks|waiting|results)\/?$/.test(pathname);
}
